import type {
    v2,
    ColorPaletteType,
    SapOvpTabSetting,
    SapOvpPlotAreaDef,
    SapOvpTimeAxisDef
} from '@sap/ux-specification-types';
import { syncRule } from '../../../common/decoration/decorators';
import { getCardSettingsPath, getOVPsettingPath } from '../../application';
import { ColorPaletteDefinitionType } from '@sap/ux-specification-types';

/**
 * Import color palette.
 *
 * @param manifestSection - The section of the manifest containing color palette information.
 * @returns The processed color palette configuration.
 */
function importColorPalette(manifestSection: object): ColorPaletteType | any[] {
    const colorPalette = manifestSection['colorPalette'];
    let configPart: ColorPaletteType | any[];
    if (Array.isArray(colorPalette)) {
        //deprecated array syntax
        throw new Error(`The array syntax for colorPalette is deprecated and not supported`);
    } else if (colorPalette['0'] && typeof colorPalette['0'] === 'object') {
        configPart = colorPalette as ColorPaletteType;
        configPart.colorPaletteType = ColorPaletteDefinitionType.SemanticColoring;
    } else if (colorPalette['dimensionSettings']) {
        configPart = colorPalette as ColorPaletteType;
        configPart.colorPaletteType = ColorPaletteDefinitionType.MapDimensionValues;
    } else {
        configPart = {
            colorPaletteType: ColorPaletteDefinitionType.StableColoring,
            dimensionValueToColor: colorPalette
        };
    }
    return configPart;
}

/**
 * Export color palette.
 *
 * @param manifestSection - The section of the manifest to be updated.
 * @param configPart - The configuration object containing the data to export.
 * @param key - The key identifying the specific configuration part.
 */
export function exportColorPalette(manifestSection: unknown, configPart: object, key: string): void {
    const value = JSON.parse(JSON.stringify(configPart[key]));
    delete value.colorPaletteType;
    if (configPart[key].colorPaletteType === ColorPaletteDefinitionType.StableColoring) {
        manifestSection[key] = value.dimensionValueToColor;
    } else if (configPart[key].colorPaletteType) {
        manifestSection[key] = value;
    }
}

/**
 * Export default span.
 *
 * @param manifestSection - The section of the manifest to be updated.
 * @param configPart - The configuration object containing the data to export.
 * @param key - The key identifying the specific configuration part.
 */
export function exportDefaultSpan(manifestSection: unknown, configPart: object, key: string): void {
    const value = configPart[key];
    if (value === 'auto') {
        manifestSection[key] = value;
    } // else = object properties, handled by own sync rules
}

export class ChartSettingsType implements v2.ChartSettingsType {
    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath() + '/chartSettings'
        }
    })
    showDataLabel?: boolean;
}

export class DefaultSpanObject implements v2.DefaultSpanObject {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    cols: number;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    rows: number;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    showOnlyHeader?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    minimumTitleRow?: number;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    minimumSubTitleRow?: number;
}

export class CommonCardSettings implements v2.CommonCardSettings {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    entitySet?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    title: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    subTitle?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    annotationPath?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    dataPointAnnotationPath?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    selectionPresentationAnnotationPath?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    kpiAnnotationPath?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    category?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    dynamicSubtitleAnnotationPath?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    valueSelectionInfo?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    showFilterInHeader?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    sortBy?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    sortOrder?: 'ascending' | 'descending';

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    identificationAnnotationPath?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    selectionAnnotationPath?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    presentationAnnotationPath?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    enableLocaleCurrencyFormatting?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys),
            export: exportDefaultSpan
        }
    })
    defaultSpan?: v2.DefaultSpanObject | 'auto';

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    requireAppAuthorization?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    showSortingInHeader?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    tabs?: SapOvpTabSetting[];

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    addODataSelect?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    showRefresh?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    customParams?: string;
}

export class CustomCard implements v2.CustomCard {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    model: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    template: v2.CardTemplateType;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    settings?: v2.CustomCardSettings;
}

export class ListCardSettings extends CommonCardSettings implements v2.ListCardSettings {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    showLineItemDetail?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    imageSupported?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    enableAddToInsights?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    listFlavor?: v2.ListFlavorType;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    listType?: v2.ListTypeType;
}

export class ListCard implements v2.ListCard {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    model: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    template: v2.CardTemplateType.list | v2.CardTemplateType.listv4;

    settings: ListCardSettings;
}

export class AnalyticalCardSettings extends CommonCardSettings implements v2.AnalyticalCardSettings {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    chartAnnotationPath?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    ignoreSapText?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    enableAddToInsights?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    navigation?: 'dataPointNav' | 'chartNav' | 'headerNav' | 'noHeaderNav';

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    chartProperties?: {
        plotArea?: SapOvpPlotAreaDef;
        timeAxis?: SapOvpTimeAxisDef;
    };

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys),
            import: importColorPalette,
            export: exportColorPalette
        }
    })
    colorPalette?: ColorPaletteType;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    bEnableStableColors?: boolean;
}

export class AnalyticalCardSettingsv4 extends AnalyticalCardSettings implements v2.AnalyticalCardSettingsv4 {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    measureAggregate?: v2.MeasureAggregateSettings;
}

export class AnalyticalCard implements v2.AnalyticalCard {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    model: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    template: v2.CardTemplateType.analytical | v2.CardTemplateType.analyticalv4;

    settings: AnalyticalCardSettings | AnalyticalCardSettingsv4;
}

export class StackCardSettings extends CommonCardSettings implements v2.StackCardSettings {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    itemText?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    objectStreamCardsNavigationProperty?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    objectStreamCardsSettings?: {
        showFirstActionInFooter?: boolean;
        annotationPath?: string;
    };
}

export class StackCard implements v2.StackCard {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    model: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    template: v2.CardTemplateType.stack;

    settings: StackCardSettings;
}

export class LinkListCardSettings extends CommonCardSettings implements v2.LinkListCardSettings {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    listFlavor?: v2.LinkListFlavorType;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    headerAnnotationPath?: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    staticContent?: v2.StaticContent[];
}

export class LinklistCard implements v2.LinklistCard {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    model: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    template: v2.CardTemplateType.linklist | v2.CardTemplateType.linklistv4;

    settings: LinkListCardSettings;
}

export class TableCardSettings extends CommonCardSettings implements v2.TableCardSettings {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    showLineItemDetail?: boolean;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    enableAddToInsights?: boolean;
}

export class TableCard implements v2.TableCard {
    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    model: string;

    @syncRule({
        manifest: {
            path: (cardKeys): string => getCardSettingsPath(cardKeys)
        }
    })
    template: v2.CardTemplateType.table | v2.CardTemplateType.tablev4;

    settings: TableCardSettings;
}
